import React from 'react';
import Icon from '../../../components/AppIcon';

const TrustSignals = () => {
  const certifications = [
    {
      name: 'SOC 2 Type II',
      description: 'Enterprise-grade security and compliance',
      icon: 'Shield',
      verified: true
    },
    {
      name: 'ISO 27001',
      description: 'Information security management certified',
      icon: 'Lock',
      verified: true
    },
    {
      name: 'GDPR Compliant',
      description: 'Full data protection compliance',
      icon: 'FileText',
      verified: true
    },
    {
      name: 'PCI DSS',
      description: 'Payment card industry security standards',
      icon: 'CreditCard',
      verified: true
    }
  ];

  const commitments = [
    {
      title: 'Non-Disclosure Agreement',
      description: 'All conversations and technical details are protected under strict confidentiality agreements.',
      icon: 'FileText',
      action: 'NDA Available'
    },
    {
      title: 'No Commitment Discovery',
      description: 'Initial discovery calls are completely free with no obligation to proceed.',
      icon: 'Clock',
      action: 'Free Consultation'
    },
    {
      title: 'Enterprise References',
      description: 'Connect with our existing clients to verify our track record and approach.',
      icon: 'Users',
      action: 'Request References'
    },
    {
      title: 'Transparent Methodology',
      description: 'Full visibility into our processes, timelines, and expected outcomes.',
      icon: 'Eye',
      action: 'View Methodology'
    }
  ];

  const clientLogos = [
    {
      name: 'TechCorp',
      logo: 'https://via.placeholder.com/120x60/2B2B2B/FFFFFF?text=TechCorp',
      industry: 'Enterprise Software'
    },
    {
      name: 'DataFlow',
      logo: 'https://via.placeholder.com/120x60/2B2B2B/FFFFFF?text=DataFlow',
      industry: 'Data Analytics'
    },
    {
      name: 'CloudScale',
      logo: 'https://via.placeholder.com/120x60/2B2B2B/FFFFFF?text=CloudScale',
      industry: 'Cloud Infrastructure'
    },
    {
      name: 'FinTech Pro',
      logo: 'https://via.placeholder.com/120x60/2B2B2B/FFFFFF?text=FinTech+Pro',
      industry: 'Financial Technology'
    },
    {
      name: 'HealthTech',
      logo: 'https://via.placeholder.com/120x60/2B2B2B/FFFFFF?text=HealthTech',
      industry: 'Healthcare Technology'
    },
    {
      name: 'RetailMax',
      logo: 'https://via.placeholder.com/120x60/2B2B2B/FFFFFF?text=RetailMax',
      industry: 'E-commerce'
    }
  ];

  const testimonials = [
    {
      quote: "SIGNA transformed our engineering velocity by 300% in just 12 weeks. Their ownership culture framework completely changed how our teams operate.",
      author: "Sarah Johnson",
      title: "CTO, TechCorp",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face"
    },
    {
      quote: "The technical debt remediation approach was surgical and effective. We reduced maintenance overhead by 60% while maintaining feature delivery.",
      author: "Michael Chen",
      title: "VP Engineering, DataFlow",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    },
    {
      quote: "Unlike other consultants, SIGNA actually embedded with our team and delivered results. Their architectural modernization saved us millions.",
      author: "Elena Rodriguez",
      title: "Head of Platform, CloudScale",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face"
    }
  ];

  return (
    <div className="space-y-12">
      {/* Security Certifications */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-text-primary mb-6">
          Enterprise Security & Compliance
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {certifications.map((cert, index) => (
            <div key={index} className="bg-card rounded-lg border border-border p-4 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-success/10 rounded-full mb-3">
                <Icon name={cert.icon} size={24} color="var(--color-success)" />
              </div>
              <h4 className="font-medium text-text-primary mb-1">{cert.name}</h4>
              <p className="text-xs text-text-secondary">{cert.description}</p>
              {cert.verified && (
                <div className="flex items-center justify-center space-x-1 mt-2">
                  <Icon name="CheckCircle" size={12} color="var(--color-success)" />
                  <span className="text-xs text-success">Verified</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Client Confidentiality Commitments */}
      <div>
        <h3 className="text-xl font-semibold text-text-primary mb-6 text-center">
          Our Commitments to You
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {commitments.map((commitment, index) => (
            <div key={index} className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-accent/10 rounded-full">
                    <Icon name={commitment.icon} size={24} color="var(--color-accent)" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-text-primary mb-2">{commitment.title}</h4>
                  <p className="text-text-secondary text-sm mb-3">{commitment.description}</p>
                  <span className="inline-flex items-center text-accent text-sm font-medium">
                    {commitment.action}
                    <Icon name="ArrowRight" size={14} className="ml-1" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Client Testimonials */}
      <div>
        <h3 className="text-xl font-semibold text-text-primary mb-6 text-center">
          What Our Clients Say
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={16} color="var(--color-warning)" />
                ))}
              </div>
              <blockquote className="text-text-secondary text-sm mb-4 italic">
                "{testimonial.quote}"
              </blockquote>
              <div className="flex items-center space-x-3">
                <img
                  src={testimonial.avatar}
                  alt={testimonial.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="font-medium text-text-primary">{testimonial.author}</div>
                  <div className="text-xs text-text-secondary">{testimonial.title}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trusted by Industry Leaders */}
      <div>
        <h3 className="text-xl font-semibold text-text-primary mb-6 text-center">
          Trusted by Industry Leaders
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {clientLogos.map((client, index) => (
            <div key={index} className="bg-card rounded-lg border border-border p-4 text-center">
              <img
                src={client.logo}
                alt={client.name}
                className="w-full h-12 object-contain mb-2"
              />
              <p className="text-xs text-text-secondary">{client.industry}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Security Notice */}
      <div className="bg-muted rounded-lg p-6 text-center">
        <div className="flex items-center justify-center space-x-2 mb-3">
          <Icon name="Shield" size={20} color="var(--color-success)" />
          <span className="font-medium text-text-primary">Secure Communication</span>
        </div>
        <p className="text-text-secondary text-sm max-w-2xl mx-auto">
          All communications are encrypted and protected. Your technical details and business information 
          are handled with the highest level of security and confidentiality. We never share client 
          information or use it for any purpose other than serving your specific needs.
        </p>
      </div>
    </div>
  );
};

export default TrustSignals;